import React, { memo, useEffect, useRef, useState } from "react";
import { array } from "prop-types";

import Anchor from "../../../atoms/Anchor";
import LeftArrowButton from "../../../atoms/icons/left-arrow-button";
import { SvgIcon as Icons } from "../../../icons";
import RightArrowButton from "../../../atoms/icons/right-arrow-button";

import { getHeadline } from "../../../utils/utils";

import "../newsHeadlineTicker.m.css";

const DesktopTicker = ({ storyList }) => {
  const tickerWrapperRef = useRef(null);
  const tickerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHover, setIsHover] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(false);
  const contentWidth = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (tickerRef.current) {
      contentWidth.current = tickerRef.current.offsetWidth + tickerWrapperRef.current.offsetWidth - 300;
    }
  }, [storyList]);

  const getOffset = () => {
    const trans = tickerRef.current.style.transform;
    const match = trans.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const hideAndStart = () => {
    setPrevDisabled(true);
    tickerRef.current.style.transition = "none";
    tickerRef.current.style.visibility = "hidden";
    tickerRef.current.style.transform = `translateX(0)`;
  };

  const move = (offsetX) => {
    setPrevDisabled(false);
    tickerRef.current.style.transition = "transform 550ms linear";
    tickerRef.current.style.visibility = "visible";
    tickerRef.current.style.transform = `translateX(-${offsetX}px)`;
  };

  useEffect(() => {
    if (!isPlaying || isHover) {
      clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      let currentOffset = getOffset();

      if (Math.abs(currentOffset) >= contentWidth.current) {
        hideAndStart();
        return;
      }

      currentOffset += 50;
      move(currentOffset);
    }, 500);

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, isHover]);

  const handleNext = () => {
    let currentOffset = getOffset();
    setIsPlaying(false);

    if (Math.abs(currentOffset) >= contentWidth.current) {
      hideAndStart();
      return;
    }

    currentOffset += 150;
    move(currentOffset);
  };

  const handlePrev = () => {
    let currentOffset = getOffset();
    setIsPlaying(false);

    if (Math.abs(currentOffset) > 150) {
      currentOffset -= 150;
      move(currentOffset);
    } else {
      tickerRef.current.style.transform = `translateX(0)`;
      setPrevDisabled(true);
    }
  };

  const handlePlayPause = () => {
    if (!isPlaying) {
      let currentOffset = getOffset();
      currentOffset += 50;
      tickerRef.current.style.transform = `translateX(-${currentOffset}px)`;
    }

    setIsPlaying((prevState) => !prevState);
  };

  return (
    <>
      <div styleName="ticker-wrapper" ref={tickerWrapperRef}>
        <div
          styleName="ticker"
          ref={tickerRef}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          {storyList.map((story, index) => (
            <Anchor
              id="ticker-item"
              key={index}
              aria-label={story.headline}
              href={story.url}
              styleName="item item-desktop"
            >
              {getHeadline(story)}
            </Anchor>
          ))}
        </div>
      </div>

      <div styleName="arrow-wrapper">
        <button styleName={prevDisabled ? "prev-disabled" : "actions"} onClick={handlePrev}>
          <LeftArrowButton opacity="1" color="none" />
        </button>
        <button styleName="actions" onClick={handlePlayPause}>
          <Icons type={isPlaying ? "pause" : "play"} />
        </button>
        <button styleName="actions" onClick={handleNext}>
          <RightArrowButton opacity="1" color="none" />
        </button>
      </div>
    </>
  );
};

DesktopTicker.propTypes = {
  storyList: array.isRequired,
};

export default memo(DesktopTicker);
